// Baseline Onboarding Flow (promoted from v2 - Deep Psychological variant)
// Builds on core pages with enhanced psychological exploration:
// - Deep motivation discovery
// - Past experience analysis
// - Cognitive style assessment
// - Identity transformation
// - Self-efficacy building
// - Accountability structures

// ============================================
// Import core pages
// ============================================
import AgePage from './AgePage'
import CraftingProgressPage from './CraftingProgressPage'
import DreamScenarioPage from './DreamScenarioPage'
import ExperienceFeedbackPage from './ExperienceFeedbackPage'
import FinalTestimonialPage from './FinalTestimonialPage'
import FreeTimeQuestionPage from './FreeTimeQuestionPage'
import GoalsQuestionPage from './GoalsQuestionPage'
import GoalsTimelinePage from './GoalsTimelinePage'
import HabitsFeedbackPage from './HabitsFeedbackPage'
import JamesTestimonialPage from './JamesTestimonialPage'
import LanguageQuestionPage from './LanguageQuestionPage'
import LevelQuestionPage from './LevelQuestionPage'
import MariaTestimonialPage from './MariaTestimonialPage'
import MistakesQuestionPage from './MistakesQuestionPage'
import ObstaclesAcknowledgePage from './ObstaclesAcknowledgePage'
import ObstaclesFeedbackPage from './ObstaclesFeedbackPage'
import PaceQuestionPage from './PaceQuestionPage'
import PersonalizationProgressPage from './PersonalizationProgressPage'
import PersonalizedPlanPreviewPage from './PersonalizedPlanPreviewPage'
import PicturingFeedbackPage from './PicturingFeedbackPage'
import PicturingQuestionPage from './PicturingQuestionPage'
import StopsQuestionPage from './StopsQuestionPage'
import StreakQuestionPage from './StreakQuestionPage'
import SubscriptionCountPage from './SubscriptionCountPage'
import TimeQuestionPage from './TimeQuestionPage'
import TopicsQuestionPage from './TopicsQuestionPage'
import WhyFeedbackPage from './WhyFeedbackPage'
import WhyQuestionPage from './WhyQuestionPage'

// ============================================
// Import deep psychology pages (promoted from v2)
// ============================================
import AccountabilityStylePage from './AccountabilityStylePage'
import BiggestFrustrationPage from './BiggestFrustrationPage'
import CognitiveStylePage from './CognitiveStylePage'
import ContentFormatPage from './ContentFormatPage'
import DeeperMotivationPage from './DeeperMotivationPage'
import FinalCommitmentPage from './FinalCommitmentPage'
import FirstMilestonePage from './FirstMilestonePage'
import IdentityShiftPage from './IdentityShiftPage'
import InvestmentReminderPage from './InvestmentReminderPage'
import LearningBlocksPage from './LearningBlocksPage'
import LearningContextPage from './LearningContextPage'
import LearningProfileSummaryPage from './LearningProfileSummaryPage'
import PastMethodsPage from './PastMethodsPage'
import SelfEfficacyPage from './SelfEfficacyPage'
import TransformationFeedbackPage from './TransformationFeedbackPage'

import navigateToNextOnboardingPageAction from '@actions/navigateToNextOnboardingPageAction'
import skipOnboardingLoader from '@app-studio-web/ui/loaders/skipOnboardingLoader'
import { type ReactElement } from 'react'
import { Outlet } from 'react-router-dom'

// The language selection question. Both the regular onboarding funnel and the
// trial funnel put it at their 'question-language' ad-entry URL (no landing
// page in front).
const languageQuestionRoute = {
  action: navigateToNextOnboardingPageAction,
  element: <LanguageQuestionPage />,
}

// Question pages after the language selection. Shared verbatim by the regular
// onboarding funnel and the trial funnel so their step order stays identical.
const coreOnboardingQuestionRoutes = [
  // ============================================
  // PHASE 1: OPENING
  // ============================================
  {
    action: navigateToNextOnboardingPageAction,
    element: <AgePage />,
    path: 'age',
  },
  {
    action: navigateToNextOnboardingPageAction,
    element: <LevelQuestionPage />,
    path: 'level-question',
  },
  // Where will you use this language?
  {
    action: navigateToNextOnboardingPageAction,
    element: <LearningContextPage />,
    path: 'learning-context',
  },
  {
    action: navigateToNextOnboardingPageAction,
    element: <WhyQuestionPage />,
    path: 'why-question',
  },
  {
    action: navigateToNextOnboardingPageAction,
    element: <WhyFeedbackPage />,
    path: 'why-feedback',
  },
  // What would it truly mean emotionally?
  {
    action: navigateToNextOnboardingPageAction,
    element: <DeeperMotivationPage />,
    path: 'deeper-motivation',
  },
  // Visualization hook
  {
    action: navigateToNextOnboardingPageAction,
    element: <DreamScenarioPage />,
    path: 'dream-scenario',
  },

  // ============================================
  // PHASE 2: GOALS & HABITS
  // ============================================
  {
    action: navigateToNextOnboardingPageAction,
    element: <GoalsQuestionPage />,
    path: 'goals-question',
  },
  {
    action: navigateToNextOnboardingPageAction,
    element: <GoalsTimelinePage />,
    path: 'goals-timeline',
  },
  // What's your first milestone?
  {
    action: navigateToNextOnboardingPageAction,
    element: <FirstMilestonePage />,
    path: 'first-milestone',
  },
  {
    action: navigateToNextOnboardingPageAction,
    element: <TimeQuestionPage />,
    path: 'time-question',
  },
  {
    action: navigateToNextOnboardingPageAction,
    element: <PaceQuestionPage />,
    path: 'pace-question',
  },
  {
    action: navigateToNextOnboardingPageAction,
    element: <HabitsFeedbackPage />,
    path: 'habits-feedback',
  },
  // Social proof after habits
  {
    action: navigateToNextOnboardingPageAction,
    element: <MariaTestimonialPage />,
    path: 'maria-testimonial',
  },

  // ============================================
  // PHASE 3: OBSTACLES
  // ============================================
  {
    action: navigateToNextOnboardingPageAction,
    element: <MistakesQuestionPage />,
    path: 'mistakes-question',
  },
  // How confident are you in your ability?
  {
    action: navigateToNextOnboardingPageAction,
    element: <SelfEfficacyPage />,
    path: 'self-efficacy',
  },
  {
    action: navigateToNextOnboardingPageAction,
    element: <StopsQuestionPage />,
    path: 'stops-question',
  },
  {
    action: navigateToNextOnboardingPageAction,
    element: <ObstaclesFeedbackPage />,
    path: 'obstacles-feedback',
  },
  // What limiting beliefs hold you back?
  {
    action: navigateToNextOnboardingPageAction,
    element: <LearningBlocksPage />,
    path: 'learning-blocks',
  },
  // Empathy acknowledgment
  {
    action: navigateToNextOnboardingPageAction,
    element: <ObstaclesAcknowledgePage />,
    path: 'obstacles-acknowledge',
  },

  // ============================================
  // PHASE 4: PERSONALIZATION
  // ============================================
  // How do you process new information?
  {
    action: navigateToNextOnboardingPageAction,
    element: <CognitiveStylePage />,
    path: 'cognitive-style',
  },
  // What methods have you tried?
  {
    action: navigateToNextOnboardingPageAction,
    element: <PastMethodsPage />,
    path: 'past-methods',
  },
  // What frustrated you most?
  {
    action: navigateToNextOnboardingPageAction,
    element: <BiggestFrustrationPage />,
    path: 'biggest-frustration',
  },
  {
    action: navigateToNextOnboardingPageAction,
    element: <ExperienceFeedbackPage />,
    path: 'experience-feedback',
  },
  {
    action: navigateToNextOnboardingPageAction,
    element: <LearningProfileSummaryPage />,
    path: 'learning-profile-summary',
  },
  {
    action: navigateToNextOnboardingPageAction,
    element: <TopicsQuestionPage />,
    path: 'topics-question',
  },
  // What content format engages you most?
  {
    action: navigateToNextOnboardingPageAction,
    element: <ContentFormatPage />,
    path: 'content-format',
  },

  // ============================================
  // PHASE 5: SOCIAL PROOF
  // ============================================
  // Progress indicator
  {
    action: navigateToNextOnboardingPageAction,
    element: <PersonalizationProgressPage />,
    path: 'personalization-progress',
  },
  // Transformation feedback
  {
    action: navigateToNextOnboardingPageAction,
    element: <TransformationFeedbackPage />,
    path: 'transformation-feedback',
  },
  // Testimonial
  {
    action: navigateToNextOnboardingPageAction,
    element: <JamesTestimonialPage />,
    path: 'james-testimonial',
  },
  {
    action: navigateToNextOnboardingPageAction,
    element: <PersonalizedPlanPreviewPage />,
    path: 'personalized-plan-preview',
  },

  // ============================================
  // PHASE 6: FINAL DETAILS
  // ============================================
  {
    action: navigateToNextOnboardingPageAction,
    element: <FreeTimeQuestionPage />,
    path: 'free-time-question',
  },
  {
    action: navigateToNextOnboardingPageAction,
    element: <PicturingQuestionPage />,
    path: 'picturing-question',
  },
  {
    action: navigateToNextOnboardingPageAction,
    element: <PicturingFeedbackPage />,
    path: 'picturing-feedback',
  },
  // Can you see yourself as a fluent speaker?
  {
    action: navigateToNextOnboardingPageAction,
    element: <IdentityShiftPage />,
    path: 'identity-shift',
  },
  {
    action: navigateToNextOnboardingPageAction,
    element: <StreakQuestionPage />,
    path: 'streak-question',
  },
  // What keeps you accountable?
  {
    action: navigateToNextOnboardingPageAction,
    element: <AccountabilityStylePage />,
    path: 'accountability-style',
  },
  // Final testimonial
  {
    action: navigateToNextOnboardingPageAction,
    element: <FinalTestimonialPage />,
    path: 'final-testimonial',
  },
  {
    action: navigateToNextOnboardingPageAction,
    element: <SubscriptionCountPage />,
    path: 'subscription-count',
  },
  {
    action: navigateToNextOnboardingPageAction,
    element: <InvestmentReminderPage />,
    path: 'investment-reminder',
  },
  {
    action: navigateToNextOnboardingPageAction,
    element: <FinalCommitmentPage />,
    path: 'final-commitment',
  },
  {
    action: navigateToNextOnboardingPageAction,
    element: <CraftingProgressPage />,
    path: 'crafting-progress',
  },
]

// Trial funnel entry: the language question stays at 'question-language' and no
// landing page is prepended. Consumed by routes.tsx (with the trial action) and
// by trialABTestRouteConfig for step ordering.
export const trialOnboardingQuestionRoutes = [
  { ...languageQuestionRoute, path: 'question-language' },
  ...coreOnboardingQuestionRoutes,
]

// The live /onboarding funnel: the 31-screen order promoted from v2 of
// li-restore-onboarding-length. It is the short funnel's order with the twelve
// psychology, feedback and commitment screens that the pre-cut flow had,
// re-inserted next to the step each one originally followed. The entry URL
// stays 'question-language'.
const step = (element: ReactElement, path: string) => ({
  action: navigateToNextOnboardingPageAction,
  element,
  path,
})

const baselineOnboardingRoutes = [
  {
    path: '',
    element: <Outlet />,
    loader: skipOnboardingLoader,
    children: [
      step(<LanguageQuestionPage />, 'question-language'),
      step(<AgePage />, 'age'),
      step(<LevelQuestionPage />, 'level-question'),
      step(<LearningContextPage />, 'learning-context'),
      step(<WhyQuestionPage />, 'why-question'),
      step(<WhyFeedbackPage />, 'why-feedback'),
      step(<DeeperMotivationPage />, 'deeper-motivation'),
      step(<DreamScenarioPage />, 'dream-scenario'),
      step(<GoalsQuestionPage />, 'goals-question'),
      step(<GoalsTimelinePage />, 'goals-timeline'),
      step(<FirstMilestonePage />, 'first-milestone'),
      step(<TimeQuestionPage />, 'time-question'),
      step(<HabitsFeedbackPage />, 'habits-feedback'),
      step(<MistakesQuestionPage />, 'mistakes-question'),
      step(<SelfEfficacyPage />, 'self-efficacy'),
      step(<ObstaclesAcknowledgePage />, 'obstacles-acknowledge'),
      step(<TopicsQuestionPage />, 'topics-question'),
      step(<MariaTestimonialPage />, 'maria-testimonial'),
      step(<CognitiveStylePage />, 'cognitive-style'),
      step(<PastMethodsPage />, 'past-methods'),
      step(<BiggestFrustrationPage />, 'biggest-frustration'),
      step(<ExperienceFeedbackPage />, 'experience-feedback'),
      step(<LearningProfileSummaryPage />, 'learning-profile-summary'),
      step(<PersonalizedPlanPreviewPage />, 'personalized-plan-preview'),
      step(<PicturingQuestionPage />, 'picturing-question'),
      step(<PicturingFeedbackPage />, 'picturing-feedback'),
      step(<IdentityShiftPage />, 'identity-shift'),
      step(<InvestmentReminderPage />, 'investment-reminder'),
      step(<FinalCommitmentPage />, 'final-commitment'),
      step(<SubscriptionCountPage />, 'subscription-count'),
      // Last step: auto-advances, and being last the action redirects to
      // /sign-up/company.
      step(<CraftingProgressPage />, 'crafting-progress'),
    ],
  },
]

export default baselineOnboardingRoutes
